import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Receipt, Printer, Send, Mail, MessageCircle, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function printInvoice(lastBill, invoiceSettings) {
  if (!lastBill) return;
  const inv = invoiceSettings || {};
  const pharmacyName = inv.pharmacy_name || 'PharmaBuddy';
  const pharmacyAddress = inv.pharmacy_address || '';
  const pharmacyPhone = inv.pharmacy_phone || '';
  const pharmacyEmail = inv.pharmacy_email || '';
  const gstNumber = inv.gst_number || '';
  const drugLicense = inv.drug_license_number || '';
  const footerText = inv.footer_text || 'Thank you for your purchase! Get well soon.';
  const showGst = inv.show_gst_breakdown !== false;
  const showDL = inv.show_drug_license !== false;

  const printWindow = window.open('', '_blank');
  const invoiceHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice - ${lastBill.bill_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
        .header h1 { margin: 0; color: #2a9d8f; font-size: 22px; }
        .header .address { margin: 5px 0; color: #666; font-size: 11px; white-space: pre-line; }
        .header .contact { margin: 5px 0; color: #555; font-size: 11px; }
        .header .license { margin: 5px 0; color: #888; font-size: 10px; }
        .bill-info { background: #f9f9f9; padding: 8px; border-radius: 4px; margin-bottom: 15px; font-size: 12px; }
        .bill-info p { margin: 3px 0; }
        .customer-info { margin-bottom: 15px; font-size: 12px; }
        .customer-info p { margin: 3px 0; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
        th, td { padding: 6px 4px; text-align: left; border-bottom: 1px solid #ddd; font-size: 11px; }
        th { background-color: #2a9d8f; color: white; }
        .totals { border-top: 2px solid #333; padding-top: 10px; }
        .totals .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 12px; }
        .totals .gst-row { color: #666; font-size: 11px; }
        .totals .grand { font-size: 16px; font-weight: bold; margin-top: 8px; padding-top: 8px; border-top: 1px solid #ccc; }
        .footer { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px dashed #ccc; color: #666; font-size: 11px; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${pharmacyName}</h1>
        ${pharmacyAddress ? '<p class="address">' + pharmacyAddress + '</p>' : ''}
        ${(pharmacyPhone || pharmacyEmail) ? '<p class="contact">' + (pharmacyPhone ? 'Tel: ' + pharmacyPhone : '') + (pharmacyPhone && pharmacyEmail ? ' | ' : '') + (pharmacyEmail ? 'Email: ' + pharmacyEmail : '') + '</p>' : ''}
        ${(gstNumber && showGst) || (drugLicense && showDL) ? '<p class="license">' + (gstNumber && showGst ? 'GST: ' + gstNumber : '') + ((gstNumber && showGst) && (drugLicense && showDL) ? ' | ' : '') + (drugLicense && showDL ? 'DL: ' + drugLicense : '') + '</p>' : ''}
      </div>
      <div class="bill-info">
        <p><strong>Invoice:</strong> ${lastBill.bill_number}</p>
        <p><strong>Date:</strong> ${new Date(lastBill.created_at).toLocaleString()}</p>
      </div>
      <div class="customer-info">
        <p><strong>Customer:</strong> ${lastBill.patient_name || 'Walk-in'}</p>
        <p><strong>Payment:</strong> ${lastBill.payment_method.toUpperCase()}</p>
        ${lastBill.hims_rx_id ? '<p><strong>HIMS RX:</strong> ' + lastBill.hims_rx_id + '</p>' : ''}
        ${lastBill.doctor_name ? '<p><strong>Doctor:</strong> ' + lastBill.doctor_name + '</p>' : ''}
      </div>
      <table>
        <thead><tr><th>Item</th><th>Qty</th><th>MRP</th><th>Amount</th></tr></thead>
        <tbody>
          ${lastBill.items.map(function(item) {
            return '<tr><td>' + item.brand_name + '</td><td>' + item.quantity + '</td><td>Rs.' + item.mrp + '</td><td>Rs.' + (item.mrp * item.quantity).toFixed(2) + '</td></tr>';
          }).join('')}
        </tbody>
      </table>
      <div class="totals">
        <div class="row"><span>Subtotal:</span><span>Rs.${lastBill.subtotal}</span></div>
        ${showGst ? '<div class="row gst-row"><span>GST:</span><span>Rs.' + lastBill.total_gst + '</span></div>' : ''}
        <div class="row"><span>Discount:</span><span>-Rs.${lastBill.total_discount}</span></div>
        <div class="row grand"><span>TOTAL:</span><span>Rs.${lastBill.grand_total}</span></div>
      </div>
      <div class="footer"><p>${footerText}</p></div>
      <script>window.onload = function() { window.print(); }</script>
    </body>
    </html>
  `;
  printWindow.document.write(invoiceHtml);
  printWindow.document.close();
  toast.success('Print dialog opened');
}

export function ReceiptDialog({ open, onOpenChange, lastBill, invoiceSettings, whatsAppEnabled }) {
  const [whatsAppNumber, setWhatsAppNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState({ whatsapp: false, email: false });

  function resetState() {
    setWhatsAppNumber('');
    setEmailAddress('');
    setInvoiceSent({ whatsapp: false, email: false });
  }

  async function sendInvoiceWhatsApp() {
    if (!lastBill || !whatsAppNumber) {
      toast.error('Please enter WhatsApp number');
      return;
    }
    setSendingWhatsApp(true);
    try {
      await axios.post(`${API}/whatsapp/send-invoice/${lastBill.id}`, null, {
        params: { to_number: whatsAppNumber }
      });
      toast.success('Invoice sent via WhatsApp!');
      setInvoiceSent(function(prev) { return { ...prev, whatsapp: true }; });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send WhatsApp');
    }
    setSendingWhatsApp(false);
  }

  async function sendInvoiceEmail() {
    if (!lastBill || !emailAddress) {
      toast.error('Please enter email address');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/send-invoice/${lastBill.id}`, null, {
        params: { to_email: emailAddress }
      });
      toast.success('Invoice sent via Email!');
      setInvoiceSent(function(prev) { return { ...prev, email: true }; });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send Email');
    }
    setSendingEmail(false);
  }

  const receiptItems = [];
  if (lastBill) {
    for (let i = 0; i < lastBill.items.length; i++) {
      const it = lastBill.items[i];
      receiptItems.push(
        <div key={i} className="receipt-row text-xs">
          <span className="truncate flex-1">{it.brand_name} x{it.quantity}</span>
          <span className="ml-2">Rs.{(it.mrp * it.quantity).toFixed(2)}</span>
        </div>
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={function(o) { if (!o) resetState(); onOpenChange(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center flex items-center justify-center gap-2">
            <Receipt className="w-5 h-5 text-mint-600" />
            Invoice Generated
          </DialogTitle>
        </DialogHeader>
        {lastBill && (
          <div className="space-y-4">
            <div className="receipt-container receipt-paper">
              <div className="receipt-header">
                <h3 className="font-bold text-lg">{invoiceSettings?.pharmacy_name || 'PharmaBuddy'}</h3>
                {invoiceSettings?.pharmacy_address && (
                  <p className="text-xs text-slate-500 whitespace-pre-line">{invoiceSettings.pharmacy_address}</p>
                )}
                {(invoiceSettings?.pharmacy_phone || invoiceSettings?.pharmacy_email) && (
                  <p className="text-xs text-slate-500">
                    {invoiceSettings.pharmacy_phone}{invoiceSettings.pharmacy_phone && invoiceSettings.pharmacy_email ? ' | ' : ''}{invoiceSettings.pharmacy_email}
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">{lastBill.bill_number}</p>
                <p className="text-xs text-slate-500">{new Date(lastBill.created_at).toLocaleString()}</p>
                {lastBill.hims_rx_id && <p className="text-xs text-mint-600">HIMS: {lastBill.hims_rx_id}</p>}
                {lastBill.patient_name && <p className="text-xs text-slate-600">Customer: {lastBill.patient_name}</p>}
              </div>
              <ScrollArea className="h-32">{receiptItems}</ScrollArea>
              <div className="receipt-total">
                <div className="receipt-row"><span>Sub</span><span>Rs.{lastBill.subtotal}</span></div>
                <div className="receipt-row text-xs"><span>GST</span><span>Rs.{lastBill.total_gst}</span></div>
                <div className="receipt-row text-xs"><span>Disc</span><span>-Rs.{lastBill.total_discount}</span></div>
                <div className="receipt-row text-lg font-bold mt-2"><span>TOTAL</span><span>Rs.{lastBill.grand_total}</span></div>
              </div>
              <div className="text-center mt-2 pt-2 border-t border-dashed text-xs text-slate-500">
                <p>Thank you!</p>
                <p>{lastBill.payment_method.toUpperCase()}</p>
              </div>
            </div>

            <div className="space-y-3 pt-3 border-t">
              <p className="text-sm font-semibold text-slate-700">Send Invoice To Customer</p>
              <Button
                className="w-full bg-slate-700 hover:bg-slate-800"
                onClick={function() { printInvoice(lastBill, invoiceSettings); }}
                data-testid="print-invoice-btn"
              >
                <Printer className="w-4 h-4 mr-2" />Print Invoice
              </Button>

              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">WhatsApp</span>
                  {invoiceSent.whatsapp && <Check className="w-4 h-4 text-green-600 ml-auto" />}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter WhatsApp number (e.g., 919876543210)"
                    value={whatsAppNumber}
                    onChange={function(e) { setWhatsAppNumber(e.target.value); }}
                    className="h-8 text-sm flex-1"
                    disabled={invoiceSent.whatsapp}
                    data-testid="whatsapp-number-input"
                  />
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={sendInvoiceWhatsApp}
                    disabled={sendingWhatsApp || invoiceSent.whatsapp || !whatsAppEnabled}
                    data-testid="send-whatsapp-btn"
                  >
                    {sendingWhatsApp ? '...' : invoiceSent.whatsapp ? <Check className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {!whatsAppEnabled && (
                  <p className="text-xs text-amber-600 mt-1">Configure Twilio in Settings to enable WhatsApp</p>
                )}
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Email</span>
                  {invoiceSent.email && <Check className="w-4 h-4 text-blue-600 ml-auto" />}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email address"
                    type="email"
                    value={emailAddress}
                    onChange={function(e) { setEmailAddress(e.target.value); }}
                    className="h-8 text-sm flex-1"
                    disabled={invoiceSent.email}
                    data-testid="email-address-input"
                  />
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={sendInvoiceEmail}
                    disabled={sendingEmail || invoiceSent.email}
                    data-testid="send-email-btn"
                  >
                    {sendingEmail ? '...' : invoiceSent.email ? <Check className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={function() { resetState(); onOpenChange(false); }}
              className="w-full"
              data-testid="close-receipt-btn"
            >
              <X className="w-4 h-4 mr-2" />Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
